import React from "react";
import CheckboxesResponse from "./CheckboxesResponse";
import CheckboxGridResponse from "./CheckboxGridResponse";
import DateTimeResponse from "./DateTimeResponse";
import DropDownResponse from "./DropDownResponse";
import EmailResponse from "./EmailResponse";
import FileUploadResponse from "./FileUploadResponse";
import LongAnswerResponse from "./LongAnswerResponse";
import MultipleChoiceGridResponse from "./MultipleChoiceGridResponse";
import MultipleChoiceResponse from "./MultipleChoiceResponse";
import NumberResponse from "./NumberResponse";
import PhoneResponse from "./PhoneResponse";
import RatingResponse from "./RatingResponse";
import ScaleResponse from "./ScaleResponse";
import ShortAnswerResponse from "./ShortAnswerResponse";
import YesNoResponse from "./YesNoResponse";
import type { SurveySummaryQuestion } from "../../types/survey.types";
import { formatDateTimeLabel } from "../../../../lib/date-time";
import { PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

interface QuestionResponseChartProps {
  question: SurveySummaryQuestion;
  totalResponses: number;
  isDarkMode: boolean;
}
const CHART_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6B7280',
];

const CHART_COLORS_DARK = [
  '#60a5fa', '#34d399', '#fbbf24', '#f87171', '#a78bfa',
  '#22d3ee', '#fb923c', '#a3e635', '#f472b6', '#9ca3af',
];

const getChartType = (question: SurveySummaryQuestion) => {
    const { type, options } = question;

    // For questions with many unique text answers, show top responses only
    if (["short_answer", "long_answer", "email", "phone", "file_upload"].includes(type)) {
      return "text_responses";
    }

    // For rating and scale questions, use bar chart
    if (["rating", "scale"].includes(type)) {
      return "bar";
    }

    // For yes/no, handled by dedicated component
    if (type === "yes_no") {
      return "none";
    }

    // For multiple choice type questions with reasonable number of options, use pie chart
    if (type === "multiple_choice" && options.length <= 6) {
      return "pie";
    }

    // For multiple answer or questions with many options, use bar chart
    if (
      ["multiple_answer", "multiple_choice_grid", "checkbox_grid"].includes(type) ||
      options.length > 6
    ) {
      return "bar";
    }

    // For date/time related questions, use bar chart
    if (["date", "time", "datetime"].includes(type)) {
      return "bar";
    }

    // For number, use bar chart if there are multiple distinct values
    if (type === "number") {
      return options.length > 1 ? "bar" : "text_responses";
    }

    // Default to bar chart
    return "bar";
  };

export default function QuestionResponseChart({ question, totalResponses, isDarkMode }: QuestionResponseChartProps) {
    // Use the new YesNoResponse component for yes/no questions
    if (question.type === "yes_no") {
      return (
        <YesNoResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new MultipleChoiceResponse component for multiple choice questions
    if (question.type === "multiple_choice") {
      return (
        <MultipleChoiceResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new DropDownResponse component for dropdown questions
    if (question.type === "dropdown") {
      return (
        <DropDownResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new CheckboxesResponse component for checkbox questions
    if (question.type === "multiple_answer") {
      return (
        <CheckboxesResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new ShortAnswerResponse component for short answer questions
    if (question.type === "short_answer") {
      return (
        <ShortAnswerResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new LongAnswerResponse component for long answer questions
    if (question.type === "long_answer") {
      return (
        <LongAnswerResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new DateTimeResponse component for date, time, and datetime questions
    if (["date", "time", "datetime"].includes(question.type)) {
      return (
        <DateTimeResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new ScaleResponse component for scale questions
    if (question.type === "scale") {
      return (
        <ScaleResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new RatingResponse component for rating questions
    if (question.type === "rating") {
      return (
        <RatingResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new FileUploadResponse component for file upload questions
    if (question.type === "file_upload") {
      return (
        <FileUploadResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new EmailResponse component for email questions
    if (question.type === "email") {
      return (
        <EmailResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new PhoneResponse component for phone questions
    if (question.type === "phone") {
      return (
        <PhoneResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new NumberResponse component for number questions
    if (question.type === "number") {
      return (
        <NumberResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new MultipleChoiceGridResponse component for multiple choice grid questions
    if (question.type === "multiple_choice_grid") {
      return (
        <MultipleChoiceGridResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    // Use the new CheckboxGridResponse component for checkbox grid questions
    if (question.type === "checkbox_grid") {
      return (
        <CheckboxGridResponse
          question={question}
          totalResponses={totalResponses}
        />
      );
    }

    const chartType = getChartType(question);
    const { options, answered_count } = question;

    // Prepare data for charts
    const chartData = options.map((option, index) => ({
      name:
        formatDateTimeLabel(option.label, question.type).length > 30
          ? formatDateTimeLabel(option.label, question.type).substring(0, 30) + "..."
          : formatDateTimeLabel(option.label, question.type),
      fullName: formatDateTimeLabel(option.label, question.type),
      value: option.count,
      percentage: answered_count > 0 ? ((option.count / answered_count) * 100).toFixed(1) : "0",
    }));

    if (chartType === "text_responses") {
      // Dynamic number of responses to show based on total responses
      const maxResponses = answered_count > 50 ? 15 : answered_count > 20 ? 10 : 5;
      const topResponses = chartData.sort((a, b) => b.value - a.value).slice(0, maxResponses);

      if (topResponses.length === 0) {
        return (
          <div className="flex items-center justify-center h-32 text-gray-600 dark:text-gray-300 font-medium">
            No responses available
          </div>
        );
      }

      // Dynamic container height based on number of responses
      const containerHeight = Math.max(200, Math.min(500, 100 + topResponses.length * 50));

      return (
        <div
          className="space-y-3"
          style={{ maxHeight: containerHeight, overflowY: "auto" }}
        >
          <div className="text-sm text-gray-700 dark:text-gray-200 mb-4 font-medium">
            Top {topResponses.length} responses (showing{" "}
            {topResponses.reduce((sum, item) => sum + item.value, 0)} of {answered_count} total
            responses)
          </div>
          {topResponses.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 border dark:border-gray-600 hover:shadow-md dark:hover:shadow-lg hover:scale-[1.02]"
            >
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                  title={item.fullName}
                  style={{
                    fontSize: topResponses.length > 10 ? "12px" : "14px",
                    lineHeight: topResponses.length > 10 ? "1.3" : "1.4",
                  }}
                >
                  {item.fullName}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-full border dark:border-blue-800/50 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors hover:scale-105">
                  {item.value}
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full border dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors hover:scale-105">
                  ({item.percentage}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (chartType === "pie") {
      // Dynamic height based on number of options
      const dynamicHeight = Math.max(300, Math.min(500, 250 + chartData.length * 15));
      const dynamicOuterRadius = Math.max(60, Math.min(100, 80 - chartData.length * 2));

      return (
        <ResponsiveContainer
          width="100%"
          height={dynamicHeight}
        >
          <RechartsPieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ percentage }: any) => `${percentage}%`}
              outerRadius={dynamicOuterRadius}
              fill="#8884d8"
              dataKey="value"
              strokeWidth={2}
              stroke={isDarkMode ? "#1f2937" : "#ffffff"}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    isDarkMode
                      ? CHART_COLORS_DARK[index % CHART_COLORS_DARK.length]
                      : CHART_COLORS[index % CHART_COLORS.length]
                  }
                  style={{
                    filter: isDarkMode
                      ? "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))"
                      : "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
                  }}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: any, name: any, props: any) => [
                `${value} responses (${props.payload.percentage}%)`,
                props.payload.fullName,
              ]}
              contentStyle={{
                backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                border: isDarkMode ? "1px solid #4b5563" : "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: isDarkMode ? "#f9fafb" : "#374151",
                boxShadow: isDarkMode
                  ? "0 10px 25px rgba(0, 0, 0, 0.3)"
                  : "0 10px 25px rgba(0, 0, 0, 0.1)",
              }}
              cursor={{
                fill: isDarkMode ? "rgba(75, 85, 99, 0.2)" : "rgba(229, 231, 235, 0.5)",
              }}
            />
            <Legend
              formatter={(value: any, entry: any) => {
                // Truncate long legend labels
                const label = entry.payload.fullName;
                return label.length > 30 ? label.substring(0, 30) + "..." : label;
              }}
              wrapperStyle={{
                fontSize: chartData.length > 8 ? "11px" : "12px",
                color: isDarkMode ? "#f9fafb" : "#374151",
                fontWeight: "500",
              }}
            />
          </RechartsPieChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === "bar") {
      // Dynamic height based on number of options
      const dynamicHeight = Math.max(300, Math.min(600, 200 + chartData.length * 25));
      const dynamicBottomMargin = Math.max(80, Math.min(150, 60 + chartData.length * 3));
      const dynamicXAxisHeight = Math.max(80, Math.min(150, 60 + chartData.length * 3));

      // Dynamic label truncation based on number of options
      const maxLabelLength = chartData.length > 10 ? 15 : chartData.length > 5 ? 20 : 25;

      return (
        <ResponsiveContainer
          width="100%"
          height={dynamicHeight}
        >
          <BarChart
            data={chartData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: dynamicBottomMargin,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={isDarkMode ? "#4b5563" : "#e5e7eb"}
              strokeOpacity={isDarkMode ? 0.3 : 0.5}
            />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={dynamicXAxisHeight}
              interval={0}
              tick={{
                fontSize: chartData.length > 15 ? 10 : 12,
                fill: isDarkMode ? "#f9fafb" : "#374151",
                fontWeight: "500",
              }}
              tickFormatter={(value) => {
                // Dynamic truncation based on number of options
                if (value.length > maxLabelLength) {
                  return value.substring(0, maxLabelLength) + "...";
                }
                return value;
              }}
            />
            <YAxis
              tick={{
                fontSize: 12,
                fill: isDarkMode ? "#f9fafb" : "#374151",
                fontWeight: "500",
              }}
            />
            <Tooltip
              formatter={(value: any, name: any, props: any) => [
                `${value} responses (${props.payload.percentage}%)`,
                props.payload.fullName,
              ]}
              contentStyle={{
                backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                border: isDarkMode ? "1px solid #4b5563" : "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: isDarkMode ? "#f9fafb" : "#374151",
                boxShadow: isDarkMode
                  ? "0 10px 25px rgba(0, 0, 0, 0.3)"
                  : "0 10px 25px rgba(0, 0, 0, 0.1)",
              }}
              cursor={{
                fill: isDarkMode ? "rgba(75, 85, 99, 0.2)" : "rgba(229, 231, 235, 0.5)",
              }}
            />
            <Bar
              dataKey="value"
              fill={isDarkMode ? "#60a5fa" : "#3B82F6"}
              radius={[4, 4, 0, 0]}
              style={{
                filter: isDarkMode
                  ? "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))"
                  : "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    return null;
  };

